// Portuguese copy for the pricing pages (Brazilian Portuguese, like src/i18n/locales/pt.json).
// Same shape as copy.en.ts (the source of truth); keep the two in step. Plan names (Lite, Pro, Max,
// Pay as you go, Enterprise) are product names and stay in English, as they do in the app.

import type { PricingCopy } from "./copy.en";

const pt: PricingCopy = {
  common: {
    perMonth: "/mês",
    onDemandFtpLink: "Como funciona o FTP a pedido",
    aMonth: "por mês",
    today: "Hoje",
    fromChangeDate: "A partir de 1º de novembro",
    changeDate: "1º de novembro de 2026",
    gb: "{n} GB",
    tb: "{n} TB",
    mostPopular: "Mais escolhido",
    renamedPlan: "{from} (agora se chama {to})",
    planFeatures: {
      free: [
        {
          text: "Sua própria marca",
          note: "Seu logo e suas cores em todas as galerias, com um pequeno selo “Powered by Photohawk”.",
        },
        "Equipe de até 3 pessoas",
        "Suporte pela central de ajuda",
        {
          text: "Servidor FTP sob demanda",
          note: "Se o FTP for indispensável, inicie seu próprio servidor pelo envio web, com um endereço Photohawk pessoal que nunca muda. Ele desliga sozinho após 1 hora sem atividade.",
        },
        {
          text: "Envio com prévia primeiro",
          note: "A galeria vai ao ar com prévias leves em minutos enquanto os originais chegam, tanto para vendas quanto para downloads gratuitos.",
        },
      ],
      pro: [
        {
          text: "Sua própria marca",
          note: "Seu logo e suas cores, sem o selo da Photohawk.",
        },
        "Domínio próprio",
        "Equipe de até 10 pessoas",
        "Análise de visitantes",
        {
          text: "Servidor FTP sob demanda",
          note: "Se o FTP for indispensável, inicie seu próprio servidor pelo envio web, com um endereço Photohawk pessoal que nunca muda. Ele desliga sozinho após 1 hora sem atividade.",
        },
        {
          text: "Envio com prévia primeiro",
          note: "A galeria vai ao ar com prévias leves em minutos enquanto os originais chegam, tanto para vendas quanto para downloads gratuitos.",
        },
        "Suporte por e-mail",
      ],
      max: [
        "Tudo do Pro",
        "Envio de vídeos",
        "Várias marcas",
        "Membros da equipe e funções ilimitados",
        "Análise de visitantes",
        {
          text: "Servidor FTP sob demanda",
          note: "Se o FTP for indispensável, inicie seu próprio servidor pelo envio web, com um endereço Photohawk pessoal que nunca muda. Ele desliga sozinho após 1 hora sem atividade.",
        },
        {
          text: "Envio com prévia primeiro",
          note: "A galeria vai ao ar com prévias leves em minutos enquanto os originais chegam, tanto para vendas quanto para downloads gratuitos.",
        },
        "Suporte prioritário",
      ],
    },
    planTaglines: {
      free: "Para começar e para eventos ocasionais.",
      pro: "Para espaços de trabalho que vendem com frequência com a própria marca.",
      max: "Para estúdios, equipes e grande volume: a menor comissão, mais armazenamento, vídeo e suporte prioritário.",
    },
    countries: {
      uk: "Reino Unido",
      my: "Malásia",
      ph: "Filipinas",
      mx: "México",
      co: "Colômbia",
      eur: "Portugal e a zona do euro",
      us: "Estados Unidos",
      ca: "Canadá",
    },
    inCountry: {
      uk: "no Reino Unido",
      my: "na Malásia",
      ph: "nas Filipinas",
      mx: "no México",
      co: "na Colômbia",
      eur: "em Portugal e na zona do euro",
      us: "nos Estados Unidos",
      ca: "no Canadá",
    },
  },

  pricing: {
    seo: {
      title: "Preços | Photohawk",
      description:
        "Comissões menores em todos os planos para fotógrafos de eventos: 16% no Lite, 14% no Pro e 12% no Max. Armazenamento e downloads gratuitos incluídos.",
    },
    notice: "Novos preços a partir de 1º de novembro de 2026.",
    noticeLink: "Veja o que muda",
    hero: {
      badge: "Preços",
      title: "Comissões menores em todos os planos.",
      titleHighlight: "A partir de {rate}.",
      subtitle:
        "Venda com 16% no Lite, 14% no Pro ou 12% no Max. As taxas de transação funcionam como hoje.",
    },
    rate: {
      heading: "Sua comissão em cada plano",
      planLabel: "no {plan}",
      points: [
        "As taxas de cartão são separadas e funcionam como hoje.",
        "Nossa comissão é a única taxa da Photohawk em uma venda.",
        "Acordos personalizados atuais continuam valendo até terminarem.",
      ],
    },
    plans: {
      heading: "Escolha seu plano",
      subtitle:
        "Planos mensais em dólares americanos, sem fidelidade. Cada plano acima reduz sua comissão e soma armazenamento e downloads gratuitos.",
      sellingLabel: "Venda",
      sellingLine: "{rate} de comissão sobre vendas",
      sellingNote: "Mais taxas de transação, como hoje",
      freeDownloadsLabel: "Downloads gratuitos",
      freeDownloadsLine: "{count} fotos por mês incluídas",
      overageLine: "depois, $8 a cada 1.000 fotos",
      storageLabel: "Armazenamento",
      storageLine: "{storage} incluídos",
      storageNote: "depois, pacotes a partir de $5 por mês",
      includedLabel: "Inclui",
      cta: "Começar",
      ctaFor: "com o {plan}",
      planChange:
        "Mudança de plano: a comissão menor de um plano vale a partir do seu primeiro mês completo nele. Mudanças para um plano inferior valem no fim do mês.",
    },
    standard: {
      heading: "Incluído em todos os planos",
      subtitle: "Todos os planos têm, inclusive o Lite, a $0 por mês.",
      items: [
        { id: "selfie", title: "Busca por selfie", body: "Seus compradores tiram uma selfie e veem todas as fotos em que aparecem." },
        { id: "bib", title: "Busca por número de peito e horário", body: "Encontre fotos pelo número de peito ou pelo horário em que foram tiradas." },
        { id: "preview", title: "Proteção de prévias", body: "Proteção contra capturas de tela com toque para revelar, para que as prévias vendam em vez de serem copiadas." },
        {
          id: "uploader",
          title: "Envio pela web e Photohawk Studio",
          body: "A forma padrão de enviar fotos: envia muitas fotos ao mesmo tempo, se adapta à sua conexão e retoma sozinho. Em todos os planos, as prévias leves podem subir primeiro para que sua galeria fique no ar em minutos. O Studio funciona hoje no seu navegador; o app Photohawk Studio para Mac, iPhone e iPad chega em novembro de 2026.",
        },
        { id: "whatsapp", title: "Entrega por WhatsApp, um serviço da Photohawk para compradores", body: "Os compradores podem pagar um pequeno valor extra no checkout para receber as fotos no WhatsApp, prontas para compartilhar. A Photohawk oferece isso em todas as galerias: você não precisa configurar, definir preço nem desativar nada." },
      ],
    },
    storage: {
      heading: "Armazenamento",
      subtitle:
        "Todos os planos incluem armazenamento. Se você guardar mais e tiver um cartão cadastrado, pacotes são adicionados automaticamente: você paga a combinação mais barata que cobre o que guarda. Sem cartão cadastrado? Novos envios ficam pausados até você adicionar um; nada é apagado e suas galerias continuam vendendo.",
      includedHeading: "Incluído",
      packsHeading: "Pacotes de armazenamento extra",
      packPrice: "${price} por mês",
      examplesHeading: "Como os pacotes se somam",
      example: "{plan} com {stored} guardados: {over} a mais, cobrado como {mix} = ${price} por mês",
      note: "Enviaremos um e-mail quando você chegar a 80% do seu armazenamento. Os pacotes saem quando você libera espaço.",
    },
    freeDownloads: {
      heading: "Galerias de download gratuito",
      subtitle:
        "Compartilhe galerias que seus compradores baixam de graça. Cada plano inclui fotos todo mês; depois são $8 a cada 1.000 fotos ($0,008 por foto) em todos os planos.",
      line: "{count} fotos por mês",
      note: "A cota recomeça todo mês, e as galerias continuam funcionando acima dela.",
    },
    example: {
      heading: "Quanto fica com você",
      subtitle: "Um pedido no Reino Unido pago com cartão, na sua própria conta Stripe, em cada plano. Teste outro valor.",
      orderLabel: "Valor do pedido",
      order: "Pedido",
      cardFee: "Taxa de cartão da Stripe",
      cardFeeNote: "Cobrada pela Stripe na sua conta, ex.: cartões do Reino Unido 1,5% + 20p",
      colPlan: "Plano",
      colCommission: "Comissão da Photohawk",
      colKeep: "Fica com você, aprox.",
      planCell: "{plan} ({rate})",
      note: "A taxa da Stripe depende do cartão: cartões internacionais e conversão de moeda costumam custar mais. Nossa comissão é a única taxa da Photohawk.",
    },
    cardFees: {
      heading: "Como funcionam as taxas de cartão",
      intro: "As taxas de cartão e de processamento de pagamentos funcionam como hoje, separadas da nossa comissão.",
      items: [
        {
          id: "stripe",
          title: "Na sua própria conta Stripe",
          body: "No Reino Unido, na UE, nos EUA e em outros países com Stripe, a Stripe cobra a taxa padrão de processamento direto na sua conta Stripe.",
        },
        {
          id: "managed",
          title: "Onde a Photohawk processa o pagamento para você",
          body: "Na Malásia, nas Filipinas, no México, na Colômbia, no Equador e em outros países onde os compradores pagam com carteiras locais, uma taxa de processamento é descontada a tarifas locais baseadas no custo, como hoje.",
        },
      ],
      linkLabel: "Ver taxas de cartão por país",
    },
    compare: {
      heading: "Compare todos os recursos",
      subtitle: "Tudo o que o Photohawk Studio inclui, plano por plano. O Studio já funciona no seu navegador em todos os planos.",
      featureColumn: "Recurso",
      included: "Incluído",
      notIncluded: "Não incluído",
      linkLabel: "Compare todos os recursos",
      sections: {
        selling: "Vendas",
        finding: "Encontrar fotos",
        galleries: "Galerias",
        brand: "Sua marca",
        insights: "Estatísticas",
        team: "Equipe",
        uploading: "Envio",
        storage: "Armazenamento",
        support: "Suporte",
      },
      rows: {
        commission: { label: "Comissão sobre as vendas", note: "Nossa única tarifa em uma venda. As taxas de cartão funcionam como hoje." },
        products: { label: "Downloads digitais, impressões e produtos", note: "Seus próprios produtos e preços." },
        packages: { label: "Pacotes e combos", note: "" },
        coupons: { label: "Cupons", note: "" },
        preorder: { label: "Preço de pré-venda", note: "" },
        whatsapp: { label: "Entrega por WhatsApp (um serviço da Photohawk para compradores)", note: "Um extra opcional que o comprador paga no checkout para receber as fotos no WhatsApp, prontas para compartilhar. Gerenciado pela Photohawk em todas as galerias; não é uma opção que você controla." },
        payouts: { label: "Repasses", note: "Para a sua própria conta Stripe, ou repassados pela Photohawk onde processamos o pagamento para você." },
        selfie: { label: "Busca por selfie", note: "" },
        bib: { label: "Busca por número de peito e por texto", note: "Encontre fotos pelo número de peito ou pelo texto que aparece na foto." },
        time: { label: "Busca por horário", note: "" },
        find: { label: "Aparece em Encontre suas fotos", note: "Os compradores encontram suas galerias públicas em photohawk.com." },
        profiles: { label: "Buscas salvas", note: "Salve uma selfie, um número ou um texto e veja as novas correspondências." },
        faceData: { label: "Dados faciais para a busca por selfie", note: "Excluídos 12 meses após o envio de uma foto, a partir de 1º de novembro de 2026." },
        galleries: { label: "Galerias e coleções", note: "Quantas você precisar." },
        private: { label: "Galerias privadas e senhas", note: "" },
        protection: { label: "Proteção de prévias", note: "Proteção contra capturas de tela com toque para revelar." },
        watermarks: { label: "Marcas d'água", note: "" },
        freeDownloads: { label: "Fotos de download gratuito incluídas", note: "A cota recomeça todo mês." },
        freeOverage: { label: "Fotos de download gratuito acima da cota", note: "As galerias continuam funcionando." },
        video: { label: "Envio de vídeos", note: "" },
        address: { label: "Seu endereço Photohawk", note: "seunome.photohawk.com" },
        customDomain: { label: "Domínio próprio", note: "Suas galerias no seu próprio endereço web." },
        pages: { label: "Páginas", note: "Suas próprias páginas junto às suas galerias." },
        emails: { label: "Modelos de e-mail personalizados", note: "Edite os e-mails que os compradores recebem de cada galeria." },
        logo: { label: "Seu logo e suas cores", note: "Nas suas galerias, páginas e e-mails." },
        noBadge: { label: "Sem o selo “Powered by Photohawk”", note: "As galerias no Lite mostram um pequeno selo." },
        live: { label: "Visitantes ao vivo", note: "Quantas pessoas estão vendo uma galeria agora." },
        analytics: { label: "Análise de visitantes", note: "Usuários e visitas ao longo do tempo, galeria por galeria." },
        sales: { label: "Pedidos e vendas ao longo do tempo", note: "" },
        collectionDownloads: { label: "Downloads por coleção", note: "" },
        people: { label: "Pessoas no seu espaço de trabalho", note: "Os colaboradores de galeria também contam. Quem já está no seu espaço de trabalho mantém o acesso." },
        roles: { label: "Funções", note: "Proprietário, administrador, acesso só para envio e acesso só para vendas." },
        collaborators: { label: "Colaboradores de galeria", note: "" },
        webUploader: { label: "Envio web", note: "Envia muitas fotos de uma vez e retoma sozinho." },
        previewsFirst: { label: "Prévia primeiro", note: "As galerias vão ao ar com prévias leves em minutos enquanto os originais chegam." },
        soldFirst: { label: "Fotos vendidas primeiro", note: "Uma foto comprada ou baixada antes de o original chegar é a próxima a ser enviada." },
        originalsBy: { label: "Data de “Originais até” para compradores", note: "Diga aos compradores quando os originais estarão prontos." },
        ftp: { label: "Servidor FTP sob demanda", note: "Se o FTP for indispensável: seu próprio servidor, iniciado pelo envio web. Ele desliga sozinho após 1 hora sem atividade." },
        cloudImport: { label: "Importar do Dropbox e do Google Drive", note: "" },
        studioApp: { label: "App Photohawk Studio para Mac, iPhone e iPad", note: "O Studio já funciona no seu navegador." },
        included: { label: "Armazenamento incluído", note: "" },
        packs: { label: "Armazenamento extra", note: "Adicionado automaticamente com um cartão cadastrado, cobrado como a combinação mais barata." },
        you: { label: "Suporte para o seu espaço de trabalho", note: "" },
        buyers: { label: "A Photohawk atende seus compradores", note: "As dúvidas dos compradores vão para o suporte da Photohawk, a menos que você defina seu próprio endereço." },
      },
      values: {
        rate: "{rate}",
        storage: "{storage}",
        perMonth: "{n} por mês",
        upToPeople: "Até {n}",
        unlimited: "Ilimitado",
        upToDomains: "Até {n}",
        months: "{n} meses",
        fromNovember: "A partir de novembro de 2026",
        packs: "A partir de ${usd} por {gb} GB por mês",
        overage: "${usd} a cada 1.000",
        supportHelp: "Central de ajuda",
        supportEmail: "E-mail",
        supportPriority: "Prioritário",
      },
    },
    faq: {
      heading: "Perguntas",
      items: [
        {
          q: "As taxas de cartão estão incluídas?",
          a: "Não. Elas são cobradas pelo provedor de pagamentos como hoje: pela Stripe na sua própria conta, ou descontadas a tarifas locais baseadas no custo onde a Photohawk processa o pagamento para você. Nossa comissão (16% no Lite, 14% no Pro, 12% no Max) é a única taxa da Photohawk.",
        },
        {
          q: "A busca por selfie está incluída?",
          a: "Sim, em todos os planos, inclusive o Lite. Assim como a busca por número de peito e horário, a proteção de prévias e o envio pela web. O app Photohawk Studio para Mac, iPhone e iPad chega a todos os planos no lançamento, em novembro de 2026.",
        },
        {
          q: "Posso mudar de plano quando quiser?",
          a: "Sim. Os planos são mensais, sem contrato anual. Se você passar para um plano com comissão menor, ela vale a partir do seu primeiro mês completo nele. Mudanças para um plano inferior valem no fim do mês.",
        },
        {
          q: "Tenho um acordo personalizado. Ele muda?",
          a: "Não. Acordos personalizados atuais continuam valendo até terminarem.",
        },
        {
          q: "O que acontece com downloads gratuitos acima da cota?",
          a: "Suas galerias de download gratuito continuam funcionando. Todo mês seu plano inclui 1.000 fotos no Lite, 5.000 no Pro e 10.000 no Max. Depois são $8 a cada 1.000 fotos ($0,008 por foto) em todos os planos.",
        },
        {
          q: "E se eu precisar de mais armazenamento?",
          a: "O Lite inclui 150 GB, o Pro 1 TB e o Max 3 TB. Acima disso, com um cartão cadastrado, pacotes são adicionados automaticamente: +100 GB por $5, +1 TB por $25 ou +5 TB por $100 por mês, cobrados como a combinação mais barata que cobre o que você guarda. Sem cartão cadastrado, novos envios ficam pausados até você adicionar um: nada é apagado e suas galerias continuam vendendo.",
        },
        {
          q: "O que vem no Max?",
          a: "A menor comissão, 12%, além de 3 TB de armazenamento, 10.000 fotos de download gratuito por mês, envio de vídeos, membros da equipe ilimitados, análise de visitantes e suporte prioritário.",
        },
        {
          q: "Quantas pessoas podem estar no meu espaço de trabalho?",
          a: "Até 3 pessoas no Lite, até 10 no Pro e quantas você quiser no Max. Já tem mais pessoas no seu espaço de trabalho? Elas mantêm o acesso: o limite só vale quando você convida alguém novo.",
        },
        {
          id: "ftp",
          q: "Ainda posso enviar por FTP?",
          a: "Sim, se o FTP for indispensável. O envio web agora é a forma padrão de enviar fotos, em todos os planos: envia muitas fotos ao mesmo tempo, muito mais do que um app de FTP como o FileZilla (2 de cada vez por predefinição, e no máximo 10), e retoma sozinho se a conexão cair. Se você ainda precisar de FTP, cada pessoa do seu espaço de trabalho, em todos os planos, pode iniciar seu próprio servidor FTP sob demanda pelo envio web, com um endereço Photohawk pessoal que nunca muda; ele desliga sozinho após 1 hora sem atividade. Os servidores FTP compartilhados serão desativados em 1º de novembro de 2026.",
        },
        {
          q: "O app Photohawk faz parte de um plano?",
          a: "Não. O Photohawk, nosso app para as fotos da sua família, é um app separado, chega em breve e não faz parte de nenhum plano do Studio. Gerencia suas galerias e envios? Isso é o Photohawk Studio: hoje funciona no seu navegador em todos os planos, e o app Studio para Mac, iPhone e iPad chega em novembro de 2026.",
        },
      ],
    },
  },

  changes: {
    seo: {
      title: "Os preços mudam em 1º de novembro de 2026 | Photohawk",
      description:
        "A partir de 1º de novembro de 2026, a comissão cai em todos os planos da Photohawk, o Pro e o Max ganham mais armazenamento e as fotos de download gratuito vêm incluídas todo mês.",
    },
    hero: {
      badge: "Mudança de preços",
      title: "Os preços mudam em 1º de novembro de 2026.",
      titleHighlight: "Comissão menor em todos os planos.",
      subtitle:
        "A comissão de todos os planos cai, o Pro e o Max ganham mais armazenamento e as fotos de download gratuito agora vêm incluídas todo mês. Você não precisa fazer nada.",
      ctaPrimary: "Ver os novos preços",
      ctaSecondary: "Compare seu plano",
    },
    why: {
      heading: "O que melhora",
      items: [
        {
          id: "rates",
          title: "Comissão menor em todos os planos",
          body: "A comissão de todos os planos cai, de 1,5 a 9 pontos. Pay as you go e Lite passam a ser o novo Lite, sem mensalidade e com 16%, o Pro vai para 14% e o Enterprise, agora chamado Max, vai para 12%.",
        },
        {
          id: "storage",
          title: "Mais armazenamento em todos os planos",
          body: "O Lite passa de 100 GB para 150 GB, o Pro de 500 GB para 1 TB e o Max de 1 TB para 3 TB. Os 25 GB do Pay as you go viram 150 GB no Lite.",
        },
        {
          id: "downloads",
          title: "Fotos de download gratuito incluídas",
          body: "Hoje cada foto de uma galeria de download gratuito é cobrada. A partir de 1º de novembro, de 1.000 a 10.000 por mês vêm incluídas e depois são $8 a cada 1.000: igual ou menos que a tarifa atual em todos os planos.",
        },
        {
          id: "simple",
          title: "Preços mais simples",
          body: "Três planos em vez de quatro, uma comissão por plano e armazenamento extra em pacotes simples.",
        },
        {
          id: "order",
          title: "Taxas de transação sem mudança",
          body: "As taxas de transação funcionam como hoje, separadas da nossa comissão.",
        },
        {
          id: "deals",
          title: "Seu acordo é respeitado",
          body: "Se você tem um acordo personalizado, ele continua valendo até terminar.",
        },
      ],
      honestHeading: "O único preço que sobe",
      honestBody:
        "O Enterprise, agora chamado Max, custa $49 por mês (antes $44,99) a partir da sua primeira renovação em ou após 1º de novembro. Em troca, inclui 3 TB em vez de 1 TB, 10.000 fotos de download gratuito por mês e a menor comissão, 12% em vez de 13,5%: a partir de cerca de $270 em vendas por mês, a comissão menor mais do que cobre a diferença. O Pro cai de $29,99 para $29.",
    },
    when: {
      heading: "O que acontece em 1º de novembro",
      subtitle: "Uma única data para todos. Seu espaço de trabalho passa para o novo plano automaticamente.",
      moves: {
        payg: "Continua custando $0 por mês. 16% em vez de 25%, e 150 GB em vez de 25 GB.",
        lite: "O Lite continua, e agora é gratuito: chega de $16,99 por mês, 16% em vez de 18,5%, e mais armazenamento: 150 GB em vez de 100 GB. Se precisar de mais, armazenamento extra a partir de $5 a cada 100 GB por mês. Suas galerias mantêm seu logo e suas cores, com um pequeno selo “Powered by Photohawk”.",
        pro: "$29 em vez de $29,99 por mês, 14% em vez de 16%, e 1 TB em vez de 500 GB.",
        enterprise: "O Enterprise passa a se chamar Max: o mesmo plano com um nome novo, agora com 3 TB e comissão de 12%. Custa $49 por mês em vez de $44,99 a partir da sua primeira renovação em ou após 1º de novembro.",
      },
      points: [
        {
          id: "action",
          title: "Nada a fazer",
          body: "Seu espaço de trabalho muda em 1º de novembro de 2026. Galerias, preços e repasses continuam normalmente.",
        },
        {
          id: "month",
          title: "A regra do mês completo",
          body: "Se você passar para um plano com comissão menor, ela vale a partir do seu primeiro mês completo nele. Mudanças para um plano inferior valem no fim do mês.",
        },
        {
          id: "change",
          title: "Mudar de plano",
          body: "Você pode mudar de plano quando quiser em Faturamento, no seu espaço de trabalho.",
        },
        {
          id: "deals",
          title: "Acordos personalizados",
          body: "Acordos personalizados atuais continuam valendo até terminarem.",
        },
        {
          id: "privacy",
          title: "Busca por selfie e privacidade",
          body: "A partir de 1º de novembro, os dados faciais usados na busca por selfie são apagados 12 meses após o envio de uma foto, para cumprir as leis de privacidade do mundo todo. Suas fotos, suas vendas e a busca por número de peito não são afetadas.",
        },
        {
          id: "team",
          title: "Sua equipe",
          body: "O Lite inclui até 3 pessoas, o Pro até 10 e o Max ilimitadas. Já tem mais pessoas no seu espaço de trabalho? Elas mantêm o acesso: o limite só vale ao adicionar alguém novo.",
        },
      ],
    },
    ftp: {
      date: "1º de novembro de 2026",
      heading: "O envio web agora é a forma padrão de enviar fotos",
      body: "Os servidores FTP compartilhados sempre ativos serão desativados em 1º de novembro de 2026. O envio web é mais rápido do que o FTP e aproveita ao máximo a sua conexão, em todos os planos. Se o FTP for indispensável, você pode iniciar seu próprio servidor sob demanda.",
      items: [
        {
          id: "web",
          title: "O envio pela web, em todos os planos",
          body: "Envia muitas fotos ao mesmo tempo, muito mais do que um app de FTP como o FileZilla (2 de cada vez por predefinição, e no máximo 10), e ajusta continuamente quantas envia para aproveitar ao máximo a sua largura de banda, e se a conexão cair, ele retoma sozinho. Em todos os planos, as prévias leves podem subir primeiro, para que sua galeria fique no ar em minutos.",
        },
        {
          id: "ondemand",
          title: "Se o FTP for indispensável: em todos os planos",
          body: "Já disponível, em todos os planos: inicie seu próprio servidor FTP sob demanda pelo envio web, com um endereço Photohawk pessoal que nunca muda. Ele desliga sozinho após 1 hora sem atividade.",
        },
        {
          id: "studio",
          title: "Photohawk Studio, em todos os planos",
          body: "O app de envio da Photohawk para o seu espaço de trabalho, para desktop e iOS.",
        },
      ],
    },
    privacyLink: "Política de retenção de dados biométricos",
    included: {
      heading: "O que cada plano inclui",
      everyPlan:
        "Em todos os planos: busca por selfie, busca por número de peito e por horário, proteção de prévias, o envio web com prévia primeiro e seu próprio servidor FTP sob demanda.",
      upgrades: "Pro e Max adicionam análise de visitantes e domínio próprio. O Max adiciona o envio de vídeos.",
    },
    compare: {
      heading: "Hoje e a partir de 1º de novembro",
      subtitle: "Plano a plano, em dólares americanos.",
      rows: {
        fee: "Mensalidade",
        commission: "Comissão sobre vendas",
        storage: "Armazenamento incluído",
        downloads: "Fotos de download gratuito",
        extraStorage: "Armazenamento extra",
      },
      downloadsToday: "{price} cada",
      downloadsNext: "{count} por mês incluídas, depois $8 a cada 1.000",
      extraStorageToday: "$0,023 por GB por mês",
      extraStorageNext: "Pacotes: +100 GB $5, +1 TB $25, +5 TB $100 por mês",
    },
    countries: {
      heading: "O que muda onde você vende",
      subtitle: "Exemplos na sua moeda. Escolha um país.",
      selectLabel: "País",
      examplesBadge: "Exemplos",
      orderHeading: "Um pedido típico: {amount}",
      orderSubtitle: "O pedido médio {inCountry}, pago com cartão. Taxa de cartão {fee}, {kind}.",
      kind: {
        stripe: "cobrada pela Stripe na sua conta",
        managed: "descontada onde a Photohawk processa o pagamento",
      },
      colPlan: "Seu plano",
      colCommission: "Comissão da Photohawk",
      colKeep: "Fica com você",
      perOrderMore: "+{amount} por pedido",
      monthlyHeading: "Um mês de vendas, tudo incluído",
      monthlySubtitle:
        "Mensalidade, comissão e taxas de cartão juntas, como parte das suas vendas, no plano que custa menos em cada nível.",
      level: "{amount} por mês em vendas",
      onPlan: "no {plan}",
      keepMore: "Fica com você cerca de {amount} a mais por mês",
      disclaimer:
        "Apenas exemplos, com base no pedido médio e convertidos a taxas de câmbio fixas (outubro de 2026). Seus números dependem dos seus pedidos, cartões e plano. Cobranças de downloads gratuitos e armazenamento não estão incluídas.",
    },
    faq: {
      heading: "Perguntas",
      items: [
        {
          q: "Preciso fazer alguma coisa?",
          a: "Não. Em 1º de novembro de 2026 seu espaço de trabalho passa para o novo plano automaticamente. Galerias, preços e repasses continuam normalmente.",
        },
        {
          q: "Estou no Lite. O que acontece?",
          a: "O Lite continua, e agora é gratuito: a partir de 1º de novembro, chega de $16,99 por mês, sua comissão é de 16% em vez de 18,5%, e você ganha mais armazenamento: 150 GB em vez de 100 GB. Se você guardar mais, o armazenamento extra custa a partir de $5 a cada 100 GB por mês e é adicionado automaticamente se você tiver um cartão cadastrado (sem cartão, novos envios ficam pausados até você adicionar um; nada é apagado). Suas galerias mantêm seu logo e suas cores, com um pequeno selo “Powered by Photohawk”.",
        },
        {
          q: "E se eu guardar mais do que meu plano inclui?",
          a: "Com um cartão cadastrado, pacotes de armazenamento são adicionados automaticamente, a partir de $5 a cada 100 GB por mês, cobrados como a combinação mais barata que cobre o que você guarda. Sem cartão cadastrado, novos envios ficam pausados até você adicionar um: nada é apagado e suas galerias continuam vendendo.",
        },
        {
          q: "Estou no Enterprise. O que acontece?",
          a: "O Enterprise passa a se chamar Max: o mesmo plano com um nome novo. A partir de 1º de novembro sua comissão é de 12% em vez de 13,5% e você tem 3 TB em vez de 1 TB. O novo preço, $49 por mês em vez de $44,99, vale a partir da sua primeira renovação em ou após 1º de novembro.",
        },
        {
          q: "Quantas pessoas podem estar no meu espaço de trabalho?",
          a: "Até 3 pessoas no Lite, até 10 no Pro e quantas você quiser no Max. Já tem mais pessoas no seu espaço de trabalho? Elas mantêm o acesso: o limite só vale quando você convida alguém novo.",
        },
        {
          id: "ftp",
          q: "O que acontece com o FTP?",
          a: "Os servidores FTP compartilhados sempre ativos serão desativados em 1º de novembro de 2026, e o envio web agora é a forma padrão de enviar fotos, em todos os planos. Se o FTP for indispensável, cada pessoa do seu espaço de trabalho, em todos os planos, já pode iniciar seu próprio servidor FTP sob demanda pelo envio web, com um endereço Photohawk pessoal que nunca muda; ele desliga sozinho após 1 hora sem atividade.",
        },
        {
          q: "Meu acordo personalizado muda?",
          a: "Não. Acordos personalizados atuais continuam valendo até terminarem.",
        },
        {
          q: "Se eu mudar de plano, quando começa a nova comissão?",
          a: "A comissão menor de um plano vale a partir do seu primeiro mês completo nele. Mudanças para um plano inferior valem no fim do mês.",
        },
        {
          q: "As taxas de cartão mudam?",
          a: "Não. As taxas de cartão e de processamento de pagamentos funcionam como hoje, separadas da nossa comissão.",
        },
        {
          q: "O app Photohawk faz parte de um plano?",
          a: "Não. O Photohawk, nosso app para as fotos da sua família, é um app separado, chega em breve e não faz parte de nenhum plano do Studio. Gerencia suas galerias e envios? Isso é o Photohawk Studio: hoje funciona no seu navegador em todos os planos, e o app Studio para Mac, iPhone e iPad chega em novembro de 2026.",
        },
      ],
    },
    cta: {
      heading: "Veja os novos preços completos",
      body: "Planos, armazenamento, downloads gratuitos e taxas de cartão por país, tudo em uma página.",
      button: "Ver os novos preços",
    },
  },

  app: {
    badge: "Em breve",
    platform: "Primeiro no iPhone",
    heading: "Photohawk, o app para as fotos da sua família",
    intro: "O Photohawk é o nosso app para todas as fotos da sua família: as que você tira e as que você compartilha. É um app separado, chega em breve e não faz parte dos planos do Studio.",
    points: [
      { id: "private", title: "Encontre as fotos da sua família, com privacidade", body: "Organize e encontre fotos das pessoas que você ama, direto no seu celular." },
      { id: "albums", title: "Álbuns em que cada um vê as próprias fotos", body: "Compartilhe um álbum e cada pessoa vê só as fotos em que aparece, pela selfie: a mesma tecnologia das galerias da Photohawk." },
      { id: "event", title: "Reúna as fotos de todos de um evento", body: "Junte em um só lugar as fotos que amigos e família tiraram." },
      { id: "prints", title: "Impressões e fotolivros", body: "Transforme as melhores em impressões e fotolivros." },
    ],
    cta: "Avise-me",
    ctaNote: "A lista de espera abre em breve.",
    phoneAlbum: "Só eu",
    phoneAlbumCount: "12 fotos",
  },
};

export default pt;
